import { cn } from '../../lib/cn.js'
import { Text } from '../ui/Typography.js'
import { Stack, RowBetween } from '../layout/Stack.js'

export interface CapacityMeterProps {
  taken: number
  /** Null means no cap — and that is not the same as a cap of nought. */
  capacity: number | null
  /** What is being counted. "places", "seats". */
  noun?: string
}

/**
 * How full something is. 9.6a draws it at the top of the class editor.
 *
 * **No cap is drawn as words, not as an empty bar.** The class screens say *"a
 * class with a cap says how many places are left; one without says **Open to
 * all** rather than inventing a number"* — and a bar at 0% for a class anybody can
 * join says the opposite of the truth.
 *
 * Full is Pêche rather than the error ink: a full class is a good outcome and a
 * limit reached, which is exactly what that colour is for in this brand.
 */
export function CapacityMeter({ taken, capacity, noun = 'places' }: CapacityMeterProps) {
  /* "1 places taken" and "1 of 1 places" were on the classes table. The noun
     is given plural, so one of them drops the `s`. */
  const counted = (n: number) => (n === 1 ? noun.replace(/s$/, '') : noun)
  if (capacity === null) {
    return (
      <Stack gap={1}>
        <Text variant="body-strong">Open to all</Text>
        <Text variant="caption">
          {taken} {counted(taken)} taken · no cap
        </Text>
      </Stack>
    )
  }

  const left = Math.max(0, capacity - taken)
  const share = capacity === 0 ? 1 : Math.min(1, taken / capacity)
  const full = left === 0

  return (
    <Stack gap={2}>
      <RowBetween align="baseline">
        <Text variant="body-strong">
          {taken} of {capacity} {counted(capacity)}
        </Text>
        <Text variant="caption">{full ? 'Full' : `${left} left`}</Text>
      </RowBetween>
      <div className="h-2 w-full overflow-hidden rounded-full bg-sunk">
        <div
          role="progressbar"
          aria-valuenow={taken}
          aria-valuemin={0}
          aria-valuemax={capacity}
          aria-label={`${taken} of ${capacity} ${counted(capacity)}`}
          className={cn(
            'h-full rounded-full transition-[width]',
            full ? 'bg-amber-fill' : 'bg-teal-fill',
          )}
          style={{ width: `${share * 100}%` }}
        />
      </div>
    </Stack>
  )
}
